import { useCallback, useEffect, useMemo, useState } from 'react';
import { IS_SERVER } from '../ssr';
import { useLocalStorage } from './useLocalStorage';
import { useMediaQuery } from './useMediaQuery';

interface IBeforeInstallPrompt {
  readonly platforms: string[];
}

interface IBeforeInstallPromptEvent extends IBeforeInstallPrompt, Event {
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Must be cached because beforepromptinstall only runs once per page
let cachedEvent: IBeforeInstallPromptEvent | IBeforeInstallPrompt | null = null;
let cachedInstalled = false;

export function useAddToHomescreenPrompt() {
  const [hideAppPromptTime, setHideAppPromptTime] = useLocalStorage<string | number | undefined>(
    'hideappprompt',
    undefined,
  );

  // Sticky "this origin has the app installed" flag. The installed PWA and
  // browser tabs share localStorage on the same origin, so the first time the
  // app runs standalone we record it here — and from then on a regular browser
  // tab knows the user already has it, even when getInstalledRelatedApps()
  // can't see an install made before related_applications was added.
  const [pwaSeenInstalled, setPwaSeenInstalled] = useLocalStorage<string | number | undefined>(
    'pwainstalled',
    undefined,
  );

  const [promptable, setPromptable] = useState<IBeforeInstallPromptEvent | null>(
    cachedEvent as IBeforeInstallPromptEvent,
  );

  const isPwa = useMediaQuery('(display-mode: standalone)', true);
  const [isInstalled, setIsInstalled] = useState(isPwa || cachedInstalled || !!pwaSeenInstalled);

  // Running standalone proves it's installed — remember it for later tab visits.
  // The flag also propagates to already-open tabs via the storage event, so
  // reacting to pwaSeenInstalled here clears the banner live (no reload needed)
  // the moment the user opens the installed app in parallel.
  useEffect(() => {
    if (IS_SERVER) {
      return;
    }

    if (isPwa) {
      setIsInstalled(true);
      cachedInstalled = true;
      if (!pwaSeenInstalled) {
        setPwaSeenInstalled(Date.now());
      }
    } else if (pwaSeenInstalled) {
      setIsInstalled(true);
      cachedInstalled = true;
    }
  }, [isPwa, pwaSeenInstalled]);

  const promptType = useMemo(() => {
    if (promptable?.prompt) {
      return 'app' as const;
    }

    if (!IS_SERVER && promptable?.platforms?.[0]?.startsWith('safari:')) {
      return promptable?.platforms?.[0] as 'safari:macos' | 'safari:ios';
    }
  }, [promptable]);

  const promptToInstall = useCallback(() => {
    if (promptable?.prompt) {
      return promptable.prompt();
    }
    return Promise.reject(
      new Error('Tried installing before browser sent "beforeinstallprompt" event'),
    );
  }, [promptable]);

  useEffect(() => {
    const ready = (e: IBeforeInstallPromptEvent) => {
      // If we already know the app is installed, don't hijack the event. Calling
      // preventDefault() without ever prompting makes Chrome log a "Banner not
      // shown: preventDefault() called" warning and would briefly stash an event
      // we never use — so just let Chrome handle it (no intrusive UI on desktop).
      const alreadyInstalled =
        !IS_SERVER &&
        (window.matchMedia('(display-mode: standalone)').matches ||
          cachedInstalled ||
          !!window.localStorage.getItem('pwainstalled'));

      if (alreadyInstalled) {
        return;
      }

      cachedEvent = e;

      e.preventDefault();
      setPromptable(e);
    };

    if (!IS_SERVER) {
      window.addEventListener('beforeinstallprompt', ready as EventListenerOrEventListenerObject);

      return () => {
        window.removeEventListener(
          'beforeinstallprompt',
          ready as EventListenerOrEventListenerObject,
        );
      };
    }
  }, []);

  useEffect(() => {
    const onInstall = () => {
      setIsInstalled(true);
      cachedInstalled = true;
    };

    if (!IS_SERVER) {
      window.addEventListener('appinstalled', onInstall as EventListenerOrEventListenerObject);

      if (
        !isPwa &&
        // Chrome/Chromium also includes "Safari" in its UserAgent... fun
        !/Chrom/i.test(navigator.userAgent) &&
        !/Edge/i.test(navigator.userAgent) &&
        /Safari/i.test(navigator.userAgent)
      ) {
        const platform = `${
          typeof navigator !== 'undefined' &&
          (navigator.userAgentData?.platform || navigator.platform)
        }`;

        const isIos = platform.startsWith('i');

        cachedEvent = {
          platforms: [isIos ? 'safari:ios' : 'safari:macos'],
        };
        setPromptable(cachedEvent as IBeforeInstallPromptEvent);
      }

      return () => {
        window.removeEventListener('appinstalled', onInstall as EventListenerOrEventListenerObject);
      };
    }
  }, []);

  // display-mode: standalone only tells us we're *running inside* the PWA, not
  // whether it's installed. When viewing in a normal browser tab, ask Chrome
  // directly whether our PWA is already installed so we don't nag to install
  // something the user already has. (Chromium-only; resolves empty elsewhere.)
  useEffect(() => {
    if (IS_SERVER) {
      return;
    }

    const nav = navigator as Navigator & {
      getInstalledRelatedApps?: () => Promise<Array<{ platform?: string }>>;
    };

    if (typeof nav.getInstalledRelatedApps !== 'function') {
      return;
    }

    let cancelled = false;

    nav
      .getInstalledRelatedApps()
      .then((apps) => {
        if (cancelled) {
          return;
        }
        if (apps?.some((app) => app.platform === 'webapp')) {
          setIsInstalled(true);
          cachedInstalled = true;
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  const ret = useMemo(
    () => ({
      promptType,
      promptable,
      promptToInstall,
      isInstalled,
      hideAppPromptTime,
      setHideAppPromptTime,
    }),
    [promptType, promptable, promptToInstall, isInstalled, hideAppPromptTime],
  );

  return ret;
}
